<script lang="ts" setup>
import { onMounted, onBeforeUnmount, ref, computed } from 'vue'

interface Props {
    threshold?: number   // px để hiện nút
    right?: number
    bottom?: number
    size?: number
    zIndex?: number
}
const props = withDefaults(defineProps<Props>(), {
    threshold: 240,
    right: 20,
    bottom: 20,
    size: 44,
    zIndex: 50
})

const visible = ref(false)
let ticking = false

const onScroll = () => {
    if (!ticking) {
        ticking = true
        requestAnimationFrame(() => {
            visible.value = window.scrollY > props.threshold
            ticking = false
        })
    }
}

const prefersReducedMotion = ref(false)
onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    prefersReducedMotion.value = mq.matches
    const handler = (e: MediaQueryListEvent) => (prefersReducedMotion.value = e.matches)
    mq.addEventListener?.('change', handler)

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', onScroll)
        mq.removeEventListener?.('change', handler)
    })
})

const styleVars = computed(() => ({
    '--stt-right': props.right + 'px',
    '--stt-bottom': props.bottom + 'px',
    '--stt-size': props.size + 'px',
    '--stt-z': String(props.zIndex)
}))

function scrollToTop() {
    const behavior: ScrollBehavior = prefersReducedMotion.value ? 'auto' : 'smooth'
    window.scrollTo({ top: 0, left: 0, behavior })
}
</script>

<template>
    <button
        v-show="visible"
        :style="styleVars"
        c2c-0 class="stt"
        type="button"
        aria-label="Scroll to top"
        title="Back to top"
        @click="scrollToTop"
    >
        <slot>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M12 4l-7 7h4v9h6v-9h4z" fill="currentColor"/>
            </svg>
        </slot>
    </button>
</template>

<style scoped>
.stt {
    position: fixed;
    right: var(--stt-right);
    bottom: var(--stt-bottom);
    width: var(--stt-size);
    height: var(--stt-size);
    z-index: var(--stt-z);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    border: none;
    background: rgba(0,0,0,.7);
    color: #fff;
    cursor: pointer;
    box-shadow: 0 6px 20px rgba(0,0,0,.25);
    transition: transform .18s ease, opacity .18s ease, visibility .18s ease;
    opacity: .4;
}
.stt:hover { transform: translateY(-2px); }
.stt:active { transform: translateY(0); }
.stt[style*="display: none"] {
    opacity: 0; visibility: hidden;
}
.stt:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 3px;
}
</style><style lang="scss" scoped></style>